/**
 * Vapaus Product Information Section
 * Handles product info section functionality including variant picker and carousel
 * Note: Add to cart functionality is now handled by Shopify Buy Button SDK
 */

class VapausProductInfo {
  constructor(container) {
    this.container = container;
    this.variantPickers = [];
    this.init();
  }
  
  init() {
    this.bindEvents();
    this.initVariantPickers();
  }
  
  bindEvents() {
    // Handle smooth scrolling for "Read more" links
    this.container.addEventListener('click', (event) => {
      if (event.target.matches('.vapaus-product-description__read-more')) {
        event.preventDefault();
        this.scrollToProductDetails();
      }
    });
  }
  
  initVariantPickers() {
    const variantPickers = this.container.querySelectorAll('[data-vapaus-variant-picker]');
    
    variantPickers.forEach(picker => {
      const variantPicker = new VapausVariantPicker(picker, {
        productFormId: picker.dataset.productFormId,
        priceContainer: picker.dataset.priceContainer,
        enableAnalytics: picker.dataset.enableAnalytics !== 'false',
        onVariantChange: (variant) => this.handleVariantChange(variant)
      });
      
      this.variantPickers.push(variantPicker);
    });
  }
  
  handleVariantChange(variant) {
    // Update Buy Button SDK with new variant
    if (window.ShopifyBuy && window.ShopifyBuy.UI) {
      // Find Buy Button components and update them
      const buyButtonContainers = document.querySelectorAll('.vapaus-buy-button-container');
      buyButtonContainers.forEach(container => {
        // The Buy Button SDK will handle variant updates automatically
        // We just need to trigger a refresh if needed
        const event = new CustomEvent('variant:change', {
          detail: { variant },
          bubbles: true
        });
        container.dispatchEvent(event);
      });
    }
    
    // Dispatch global variant change event for other components
    const event = new CustomEvent('variant:change', {
      detail: { variant },
      bubbles: true
    });
    document.dispatchEvent(event);
  }
  
  scrollToProductDetails() {
    const productDetails = document.getElementById('product-details');
    if (productDetails) {
      productDetails.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  }
}

/**
 * Vapaus Variant Picker
 * Handles variant selection, price updates, and analytics
 * Note: Cart updates are now handled by Shopify Buy Button SDK
 */

class VapausVariantPicker {
  constructor(container, options = {}) {
    this.container = container;
    this.options = {
      productFormId: null,
      priceContainer: null,
      enableAnalytics: true,
      ...options
    };
    
    this.currentVariant = null;
    this.isLoading = false;
    
    this.init();
  }
  
  init() {
    this.bindEvents();
    this.updateCurrentVariant();
    this.setupAnalytics();
  }
  
  bindEvents() {
    // Handle button clicks for variant selection
    this.container.addEventListener('click', (event) => {
      if (event.target.matches('.vapaus-variant-picker__value:not(.vapaus-variant-picker__value--disabled)')) {
        this.handleVariantButtonClick(event);
      }
    });
    
    // Handle keyboard navigation
    this.container.addEventListener('keydown', (event) => {
      this.handleKeyboardNavigation(event);
    });
  }
  
  handleVariantButtonClick(event) {
    event.preventDefault();
    
    const button = event.target;
    const option = button.dataset.option;
    const value = button.dataset.value;
    const variantId = button.dataset.variantId;
    
    if (!option || !value || !variantId) {
      console.warn('Missing variant data:', { option, value, variantId });
      return;
    }
    
    // Update button states
    this.updateButtonStates(option, value);
    
    // Update hidden input
    this.updateVariantInput(variantId);
    
    // Update current variant
    this.updateCurrentVariant();
    
    // Trigger variant change event
    this.triggerVariantChange();
    
    // Analytics
    if (this.options.enableAnalytics) {
      this.trackVariantSelection(option, value);
    }
  }
  
  updateButtonStates(selectedOption, selectedValue) {
    // Remove selected state from all buttons in this option group
    const optionGroup = this.container.querySelector(`[data-option="${selectedOption}"]`).closest('.vapaus-variant-picker__option');
    const buttons = optionGroup.querySelectorAll('.vapaus-variant-picker__value');
    
    buttons.forEach(button => {
      button.classList.remove('vapaus-variant-picker__value--selected');
    });
    
    // Add selected state to clicked button
    const selectedButton = this.container.querySelector(`[data-option="${selectedOption}"][data-value="${selectedValue}"]`);
    if (selectedButton) {
      selectedButton.classList.add('vapaus-variant-picker__value--selected');
    }
  }
  
  updateVariantInput(variantId) {
    const variantInput = this.container.querySelector('[data-variant-input]');
    if (variantInput) {
      variantInput.value = variantId;
    }
  }
  
  updateCurrentVariant() {
    const variantInput = this.container.querySelector('[data-variant-input]');
    if (variantInput) {
      const variantId = variantInput.value;
      
      // Try to get variant data from the button that was clicked
      const selectedButton = this.container.querySelector('.vapaus-variant-picker__value--selected');
      const isAvailable = selectedButton && !selectedButton.classList.contains('vapaus-variant-picker__value--disabled');
      
      this.currentVariant = {
        id: variantId,
        available: isAvailable,
        option1: selectedButton?.dataset.value || '',
        option2: selectedButton?.dataset.value || ''
      };
    }
  }
  
  triggerVariantChange() {
    // Dispatch custom event for other components to listen to
    const event = new CustomEvent('variant:change', {
      detail: {
        variant: this.currentVariant,
        picker: this
      },
      bubbles: true
    });
    
    this.container.dispatchEvent(event);
    
    // Also trigger on the document for global listeners
    document.dispatchEvent(event);
    
    // Call the onVariantChange callback
    if (this.options.onVariantChange && typeof this.options.onVariantChange === 'function') {
      this.options.onVariantChange(this.currentVariant);
    }
  }
  
  handleKeyboardNavigation(event) {
    const buttons = this.container.querySelectorAll('.vapaus-variant-picker__value:not(.vapaus-variant-picker__value--disabled)');
    const currentIndex = Array.from(buttons).findIndex(button => button === document.activeElement);
    
    let nextIndex = currentIndex;
    
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (currentIndex + 1) % buttons.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = currentIndex === 0 ? buttons.length - 1 : currentIndex - 1;
        break;
      case 'Enter':
      case ' ':
        if (currentIndex >= 0) {
          event.preventDefault();
          buttons[currentIndex].click();
        }
        return;
      default:
        return;
    }
    
    if (nextIndex >= 0 && nextIndex < buttons.length) {
      event.preventDefault();
      buttons[nextIndex].focus();
    }
  }
  
  setupAnalytics() {
    if (!this.options.enableAnalytics || typeof gtag === 'undefined') {
      return;
    }
    
    // Track initial variant selection
    this.container.addEventListener('variant:change', (event) => {
      const { variant } = event.detail;
      
      gtag('event', 'select_content', {
        content_type: 'product_variant',
        item_id: variant.id,
        value: variant.price ? (variant.price / 100) : 0
      });
    });
  }
  
  trackVariantSelection(option, value) {
    if (typeof gtag === 'undefined') {
      return;
    }
    
    gtag('event', 'select_option', {
      content_type: 'product_variant',
      option_name: option,
      option_value: value
    });
  }
}


// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  console.log('VapausProductInfo: DOM ready, initializing components');
  
  // Initialize product info sections
  const productInfoSections = document.querySelectorAll('section[id^="MainProduct-"], .vapaus-product-info-section');
  productInfoSections.forEach(section => {
    new VapausProductInfo(section);
  });
});

// Initialize for dynamic content (e.g., theme editor)
document.addEventListener('shopify:section:load', (event) => {
  console.log('VapausProductInfo: Section loaded, initializing components');
  if (event.target.id && event.target.id.startsWith('MainProduct-') || event.target.classList.contains('vapaus-product-info-section')) {
    new VapausProductInfo(event.target);
  }
});

// Also try to initialize immediately if DOM is already ready
if (document.readyState === 'loading') {
  console.log('VapausProductInfo: DOM still loading, waiting for DOMContentLoaded');
} else {
  console.log('VapausProductInfo: DOM already ready, initializing immediately');
  
  // Initialize product info sections
  const productInfoSections = document.querySelectorAll('section[id^="MainProduct-"], .vapaus-product-info-section');
  productInfoSections.forEach(section => {
    new VapausProductInfo(section);
  });
}

// Scroll to product details function
function scrollToProductDetails() {
  const productDetails = document.querySelector('.vapaus-product-details');
  if (productDetails) {
    productDetails.scrollIntoView({ behavior: 'smooth' });
  }
}

// Make function globally available
window.scrollToProductDetails = scrollToProductDetails;

// Export for use in other scripts
window.VapausProductInfo = VapausProductInfo;
window.VapausVariantPicker = VapausVariantPicker;